import { ReactComponent as ReloadIcon } from 'assett/icons/reload.svg';
import { ReactComponent as SearchHideIcon } from 'assett/icons/search_hide_icon.svg';
import { ReactComponent as SearchIcon } from 'assett/icons/search_icon.svg';
import MultipleSelect from 'components/commons/form/MultipleSelect';
import SelectGeneric from 'components/commons/form/SelectGeneric';
import HeaderCellWithSorting from 'components/commons/list/HeaderCellWithSorting';
import { useMembersBillingUrlSync } from 'components/viewBilling/billingFilterHook';
import { Dayjs } from 'dayjs';
import {
	handleChange,
	resetInputFilters,
	setSearch,
} from 'feature/form/formSlice';
import { setDownloadAll } from 'feature/tableSelectedItems/tableSelectedItemsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import {
	Box,
	Checkbox,
	IconButton,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from 'styles';
import CustomMonthRangePicker from 'styles/datePicker/CustomMonthRangePicker';
import DatePicker from 'styles/datePicker/DatePicker';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';

const MembersBillingTableHeader = () => {
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.DB_LABELS,
	});
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { search, groups } = useSelector((store: RootState) => store.form);
	const dispatch = useDispatch<AppDispatch>();

	useMembersBillingUrlSync();

	const {
		claimsBillingStatuses,
		adminBillingStatuses,
		tabSelected,
		planCodesDomain,
	} = useSelector((store: RootState) => store.billing);

	const { claimClientsDomain, relationshipTypes } = useSelector(
		(store: RootState) => store.form,
	);

	const { downloadAll } = useSelector(
		(store: RootState) => store.tableSelectedItems,
	);

	const {
		inputMemberBillingStatus,
		inputMemberBillingMonthStart,
		inputMemberBillingMonthEnd,
		inputMemberClient,
		inputMemberGroups,
		inputMemberPlanCodes,
		inputMemberRelationshipTypes,
		inputMemberSubscriptionStartDate,
		inputMemberSubscriptionEndDate,
	} = useSelector((store: RootState) => store.form);

	const billingStatuses =
		tabSelected === 'claims' ? claimsBillingStatuses : adminBillingStatuses;

	const handleCheckDownload = () => {
		dispatch(setDownloadAll(!downloadAll));
	};

	return (
		<TableHead>
			<TableRow>
				<TableCell>
					<Checkbox
						label=""
						onClick={handleCheckDownload}
						checked={downloadAll}
						sx={{ p: 0 }}
					/>
				</TableCell>
				<TableCell>
					<Box sx={{ width: 215 }}>
						<Typography variant="bodyLink">
							{tClientLabels('forms.header.carrier')}
						</Typography>
					</Box>
				</TableCell>
				<TableCell>
					<Box sx={{ width: 250 }}>
						<Typography variant="bodyLink">
							{tClientLabels('forms.header.billingType')}
						</Typography>
					</Box>
				</TableCell>
				<TableCell>
					<Box sx={{ width: 215 }}>
						<Typography variant="bodyLink">
							{tClientLabels('forms.header.billingRateAmount')}
						</Typography>
					</Box>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberBillingId"
						widthBox={215}
						widthInput={215}
					/>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberBillingStatus"
						sortField="billingStatusCode"
						widthBox={215}
						widthInput={215}
					>
						<SelectGeneric
							action={status =>
								dispatch(
									handleChange({ name: 'inputMemberBillingStatus', value: status }),
								)
							}
							items={billingStatuses.map(el => ({
								value: el.code,
								text: tDbLabels(el.label),
							}))}
							name={'memberBillingStatus'}
							initialValue={inputMemberBillingStatus}
							sxForm={{ flex: 1, width: '100%' }}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="billingMonth" widthBox={215} widthInput={215}>
						<CustomMonthRangePicker
							name="billingMonth"
							dates={[inputMemberBillingMonthStart, inputMemberBillingMonthEnd]}
							setStartDate={e =>
								dispatch(
									handleChange({
										name: 'inputMemberBillingMonthStart',
										value: e as Dayjs,
									}),
								)
							}
							setEndDate={e =>
								dispatch(
									handleChange({
										name: 'inputMemberBillingMonthEnd',
										value: e as Dayjs,
									}),
								)
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberClient"
						sortField="clientName"
						widthBox={300}
						widthInput={300}
					>
						<MultipleSelect
							items={claimClientsDomain}
							name={'client'}
							value={inputMemberClient}
							onChange={e =>
								dispatch(handleChange({ name: 'inputMemberClient', value: e }))
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberGroups"
						sortField="groupName"
						widthBox={215}
						widthInput={215}
					>
						<MultipleSelect
							items={groups}
							name={'group'}
							value={inputMemberGroups}
							onChange={e =>
								dispatch(handleChange({ name: 'inputMemberGroups', value: e }))
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting name="planCode" widthBox={215} widthInput={215}>
						<MultipleSelect
							items={planCodesDomain}
							name={'planCode'}
							value={inputMemberPlanCodes}
							onChange={e =>
								dispatch(handleChange({ name: 'inputMemberPlanCodes', value: e }))
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberDivisionCode"
						widthBox={215}
						widthInput={215}
					/>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberLastName"
						widthBox={215}
						widthInput={215}
					/>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberFirstName"
						widthBox={215}
						widthInput={215}
					/>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="relationshipType"
						widthBox={215}
						widthInput={215}
					>
						<MultipleSelect
							items={relationshipTypes}
							name={'relationshipType'}
							value={inputMemberRelationshipTypes}
							onChange={e =>
								dispatch(
									handleChange({ name: 'inputMemberRelationshipTypes', value: e }),
								)
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberMemberId"
						widthBox={215}
						widthInput={215}
					/>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberEmMemberId"
						widthBox={215}
						widthInput={215}
					/>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberSubscriptionStart"
						widthBox={215}
						widthInput={215}
					>
						<DatePicker
							name="inputMemberSubscriptionStartDate"
							value={inputMemberSubscriptionStartDate}
							onChange={e =>
								dispatch(
									handleChange({
										name: 'inputMemberSubscriptionStartDate',
										value: e as Dayjs,
									}),
								)
							}
							label={tClientLabels('commons.datePickerHeader.memberSubscriptionStart')}
							variant="field"
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<HeaderCellWithSorting
						name="memberSubscriptionEnd"
						widthBox={215}
						widthInput={215}
					>
						<DatePicker
							name="inputMemberSubscriptionEndDate"
							value={inputMemberSubscriptionEndDate}
							onChange={e =>
								dispatch(
									handleChange({
										name: 'inputMemberSubscriptionEndDate',
										value: e as Dayjs,
									}),
								)
							}
							label={tClientLabels('commons.datePickerHeader.memberSubscriptionEnd')}
							variant="field"
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell>
					<Box sx={{ width: 215 }}>
						<Typography variant="bodyLink">
							{tClientLabels('forms.header.market')}
						</Typography>
					</Box>
				</TableCell>
				<TableCell sx={{ minWidth: '56px', textAlign: 'center' }}>
					{search && (
						<IconButton onClick={() => dispatch(resetInputFilters())}>
							<ReloadIcon />
						</IconButton>
					)}
				</TableCell>
				<TableCell>
					<IconButton onClick={() => dispatch(setSearch(!search))}>
						{search ? <SearchHideIcon /> : <SearchIcon />}
					</IconButton>
				</TableCell>
			</TableRow>
		</TableHead>
	);
};

export default MembersBillingTableHeader;
